import type {
  DataDrivenPropertyValueSpecification,
  ExpressionSpecification,
  FillLayerSpecification,
  LineLayerSpecification,
  VectorSourceSpecification,
} from 'mapbox-gl';

import type { MapThemeColors } from '../utils/color-scale';
import { MAX_ZOOM, MIN_ZOOM } from '../utils/constants';
import type { ChoroplethLayerConfig } from './ChoroplethConfig';

export class LayerManager {
  private map: mapboxgl.Map | null = null;
  private highlightLayerIdsByConfigId: Map<string, string> = new Map();
  private readonly colors: MapThemeColors;

  constructor(colors: MapThemeColors) {
    this.colors = colors;
  }

  init(map: mapboxgl.Map) {
    this.map = map;
  }

  addSource(config: ChoroplethLayerConfig) {
    if (!this.map) return;
    if (!this.map.getSource(config.sourceId)) {
      if (config.sourceType === 'vector') {
        const vectorSource: VectorSourceSpecification = {
          type: 'vector',
          url: config.sourceUrl,
          promoteId: config.promoteId ?? config.propertyName,
        };
        this.map.addSource(config.sourceId, vectorSource);
      } else if (config.sourceType === 'geojson' && config.sourceUrl) {
        this.map.addSource(config.sourceId, {
          type: 'geojson',
          data: config.sourceUrl,
        });
      }
    }
  }

  addLayers(config: ChoroplethLayerConfig, labelLayerId: string) {
    if (!this.map) return;

    const baseLayerConfig: FillLayerSpecification = {
      id: config.fillLayerId,
      type: 'fill',
      source: config.sourceId,
      minzoom: config.minzoom,
      maxzoom: config.maxzoom,
      paint: {
        'fill-color': 'transparent',
        'fill-opacity': 0,
        'fill-color-transition': {
          duration: 0,
          delay: 0,
        },
        'fill-opacity-transition': {
          duration: 0,
          delay: 0,
        },
      },
      ...(config.worldviewFilter ? { filter: config.worldviewFilter } : {}),
    };

    if (!this.map.getLayer(config.fillLayerId)) {
      const fillLayerConfig: FillLayerSpecification =
        config.sourceType === 'vector'
          ? { ...baseLayerConfig, 'source-layer': config.sourceLayer }
          : baseLayerConfig;

      if (labelLayerId) {
        this.map.addLayer(fillLayerConfig, labelLayerId);
      } else {
        this.map.addLayer(fillLayerConfig);
      }
    }

    if (!this.map.getLayer(config.outlineLayerId)) {
      const outlineLineWidth: ExpressionSpecification = [
        'interpolate',
        ['linear'],
        ['zoom'],
        MIN_ZOOM,
        0.75,
        MAX_ZOOM,
        2,
      ];

      const outlineLayerConfig: LineLayerSpecification = {
        id: config.outlineLayerId,
        type: 'line',
        source: config.sourceId,
        minzoom: config.minzoom,
        maxzoom: config.maxzoom,
        paint: {
          'line-width': outlineLineWidth,
          'line-color': this.colors.border,
          'line-opacity': 1,
        },
        ...(config.worldviewFilter ? { filter: config.worldviewFilter } : {}),
      };

      const finalOutlineConfig =
        config.sourceType === 'vector'
          ? { ...outlineLayerConfig, 'source-layer': config.sourceLayer }
          : outlineLayerConfig;

      if (labelLayerId) {
        this.map.addLayer(finalOutlineConfig, labelLayerId);
      } else {
        this.map.addLayer(finalOutlineConfig);
      }

      // Dedicated highlight layer on top
      const highlightId = `${config.outlineLayerId}-hover-highlight`;
      if (!this.map.getLayer(highlightId)) {
        const highlightLineWidth: ExpressionSpecification = [
          'interpolate',
          ['linear'],
          ['zoom'],
          MIN_ZOOM,
          1,
          MAX_ZOOM,
          2,
        ];

        const highlightOpacity: ExpressionSpecification = [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          1,
          0,
        ];

        const base: LineLayerSpecification = {
          id: highlightId,
          type: 'line',
          source: config.sourceId,
          minzoom: config.minzoom,
          maxzoom: config.maxzoom,
          paint: {
            'line-color': this.colors.highlight,
            'line-width': highlightLineWidth,
            'line-opacity': highlightOpacity,
          },
          ...(config.worldviewFilter ? { filter: config.worldviewFilter } : {}),
          slot: 'top',
        };
        const finalHighlightConfig =
          config.sourceType === 'vector'
            ? { ...base, 'source-layer': config.sourceLayer }
            : base;
        this.map.addLayer(finalHighlightConfig);
        this.map.moveLayer(highlightId);
      }
      this.highlightLayerIdsByConfigId.set(config.id, highlightId);
    }
  }

  setPaint(
    config: ChoroplethLayerConfig,
    fillColorExpr: DataDrivenPropertyValueSpecification<string> | string
  ) {
    if (!this.map || !config) return;
    if (this.map.getLayer(config.fillLayerId)) {
      this.map.setPaintProperty(
        config.fillLayerId,
        'fill-color',
        fillColorExpr
      );
    }
  }

  setOpacity(
    config: ChoroplethLayerConfig,
    fillOpacity: number,
    lineOpacity: number
  ) {
    if (!this.map) return;

    if (this.map.getLayer(config.fillLayerId)) {
      this.map.setPaintProperty(
        config.fillLayerId,
        'fill-opacity',
        fillOpacity
      );
    }

    if (this.map.getLayer(config.outlineLayerId)) {
      this.map.setPaintProperty(
        config.outlineLayerId,
        'line-opacity',
        lineOpacity
      );
    }
  }

  setZoomRange(
    config: ChoroplethLayerConfig,
    minzoom: number,
    maxzoom: number
  ) {
    if (!this.map) return;
    if (this.map.getLayer(config.fillLayerId)) {
      this.map.setLayerZoomRange(config.fillLayerId, minzoom, maxzoom);
    }
    if (this.map.getLayer(config.outlineLayerId)) {
      this.map.setLayerZoomRange(config.outlineLayerId, minzoom, maxzoom);
    }
  }

  removeAll(configs: ChoroplethLayerConfig[]) {
    if (!this.map) return;
    configs.forEach((config) => {
      const hlId = this.highlightLayerIdsByConfigId.get(config.id);
      if (hlId && this.map?.getLayer(hlId)) {
        this.map.removeLayer(hlId);
      }
      if (this.map?.getLayer(config.fillLayerId)) {
        this.map.removeLayer(config.fillLayerId);
      }
      if (this.map?.getLayer(config.outlineLayerId)) {
        this.map.removeLayer(config.outlineLayerId);
      }
      if (this.map?.getSource(config.sourceId)) {
        this.map.removeSource(config.sourceId);
      }
    });
    this.highlightLayerIdsByConfigId.clear();
  }

  setHoverState(
    config: ChoroplethLayerConfig,
    featureId: string | number,
    isHovered: boolean
  ) {
    if (!this.map) return;
    const hlId = this.highlightLayerIdsByConfigId.get(config.id);
    if (!hlId || !this.map.getLayer(hlId)) return;

    const target: {
      source: string;
      sourceLayer?: string;
      id: string | number;
    } = {
      source: config.sourceId,
      id: featureId,
    };

    if (config.sourceType === 'vector' && config.sourceLayer) {
      target.sourceLayer = config.sourceLayer;
    }

    this.map.setFeatureState(target, { hover: isHovered });
    this.map.moveLayer(hlId);
  }
}
